import React, { useState, useEffect } from 'react';
import { stationApi, formatApiError } from '../services/api';
import { MapPin, Plus, PencilSimple, Trash, Phone, EnvelopeSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  address: '',
  postal_code: '',
  city: '',
  phone: '',
  email: '',
  notes: ''
};

const Stations = () => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await stationApi.getAll();
      setStations(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.email) delete payload.email;
      if (editing) {
        await stationApi.update(editing.id, payload);
        toast.success('Station aktualisiert');
      } else {
        await stationApi.create(payload);
        toast.success('Station angelegt');
      }
      setShowModal(false);
      setEditing(null);
      setFormData(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleEdit = (station) => {
    setEditing(station);
    setFormData({
      name: station.name || '',
      address: station.address || '',
      postal_code: station.postal_code || '',
      city: station.city || '',
      phone: station.phone || '',
      email: station.email || '',
      notes: station.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Station wirklich löschen?')) return;
    try {
      await stationApi.delete(id);
      toast.success('Station gelöscht');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  return (
    <div data-testid="stations-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <p className="overline text-gray-500 mb-2">Verwaltung</p>
          <h1 className="h1 text-[#1a1a1a]">Stationen</h1>
          <p className="body text-gray-600 mt-1">Abhol- und Rückgabestandorte für Ihre Mietfahrzeuge</p>
        </div>
        <Button className="btn-primary" onClick={openNew} data-testid="add-station-btn">
          <Plus size={18} className="mr-2" />
          Neue Station
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Lade Stationen…</div>
      ) : stations.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="h3 mb-2">Noch keine Stationen</h3>
          <p className="body text-gray-600 mb-6">
            Legen Sie Ihre Standorte (z. B. Heimsheim, Stuttgart) an, damit Sie diese im Mietvertrag als Abhol- und Rückgabeort verwenden können.
          </p>
          <Button className="btn-primary" onClick={openNew} data-testid="empty-add-station-btn">
            <Plus size={18} className="mr-2" />
            Erste Station anlegen
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stations.map(station => (
            <div key={station.id} className="card-swiss p-5 hover:border-[#E60000] transition-colors" data-testid={`station-card-${station.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-sm bg-red-50 flex items-center justify-center">
                    <MapPin size={20} weight="duotone" className="text-[#E60000]" />
                  </div>
                  <h3 className="h3 text-[#1a1a1a]">{station.name}</h3>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(station)}
                    className="p-1.5 rounded-sm hover:bg-gray-100 text-gray-600"
                    title="Bearbeiten"
                    data-testid={`edit-station-${station.id}`}
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(station.id)}
                    className="p-1.5 rounded-sm hover:bg-red-50 text-red-600"
                    title="Löschen"
                    data-testid={`delete-station-${station.id}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-700 space-y-1">
                <div>{station.address}</div>
                <div>{station.postal_code} {station.city}</div>
                {station.phone && (
                  <div className="flex items-center gap-1.5 text-gray-500 pt-2">
                    <Phone size={14} />
                    <span>{station.phone}</span>
                  </div>
                )}
                {station.email && (
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <EnvelopeSimple size={14} />
                    <span className="truncate">{station.email}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Station bearbeiten' : 'Neue Station'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="overline">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="input-swiss"
                placeholder="z. B. Station Heimsheim"
                required
                data-testid="station-name-input"
              />
            </div>
            <div>
              <Label className="overline">Adresse *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="input-swiss"
                placeholder="Straße und Hausnummer"
                required
                data-testid="station-address-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="overline">PLZ *</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  className="input-swiss"
                  required
                  data-testid="station-postal-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="overline">Ort *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="input-swiss"
                  required
                  data-testid="station-city-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-swiss"
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label className="overline">E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-swiss"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div>
              <Label className="overline">Notizen</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="input-swiss w-full px-3 py-2 min-h-[60px]"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-station-btn">
                {editing ? 'Speichern' : 'Anlegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Stations;
